import { useMutation } from '@tanstack/react-query'
import { useId, useState, type FormEvent } from 'react'
import { ConfirmButton } from '@/components/ConfirmButton'
import { FormError } from '@/components/FormError'
import { Avatar } from '@/components/tree/Avatar'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { api } from '@/lib/api'
import { paragraphs, written } from '@/lib/moments'
import type { Person, Remembrance } from '@/lib/tree'

const MAX = 2000

// "What we loved most": a few words each from anyone in the circle, on the memorial page of someone who has passed.
// They read in the order they were written. Each is its author's own to change or take back; nobody else can hide it,
// and there's nothing to count or like.
export function Remembrances({ person, circle, onDone }: { person: Person; circle: string; onDone: () => void }) {
  const first = person.name
  return (
    <section className="flex flex-col gap-8" aria-labelledby="loved-most">
      <div className="flex flex-col gap-1">
        <h2 id="loved-most" className="text-title">
          What we loved most about {first}
        </h2>
        {person.remembrances.length === 0 && (
          <p className="text-sub text-muted-foreground">
            Nothing written yet. A habit, a saying, a smell from the kitchen.
          </p>
        )}
      </div>
      {person.remembrances.length > 0 && (
        <ul className="flex flex-col gap-8">
          {person.remembrances.map((x) => (
            <li key={x.id}>
              <Words remembrance={x} onDone={onDone} />
            </li>
          ))}
        </ul>
      )}
      <Write person={person} circle={circle} onDone={onDone} />
    </section>
  )
}

function Words({ remembrance: x, onDone }: { remembrance: Remembrance; onDone: () => void }) {
  const [editing, setEditing] = useState(false)
  const remove = useMutation({
    mutationFn: () => api(`/remembrances/${x.id}`, { method: 'DELETE' }),
    onSuccess: onDone,
  })
  if (editing) {
    return (
      <WordsForm
        initial={x.body}
        label="Your words"
        submit="Save"
        onCancel={() => setEditing(false)}
        save={(body) => api(`/remembrances/${x.id}`, { method: 'PATCH', body: { body } })}
        onSaved={() => {
          setEditing(false)
          onDone()
        }}
      />
    )
  }
  return (
    <figure className="flex flex-col gap-4 border-l-2 border-border pl-5">
      <blockquote className="flex flex-col gap-3 text-lg leading-relaxed text-pretty">
        {paragraphs(x.body).map((p, i) => (
          <p key={i} className="whitespace-pre-wrap">
            {p}
          </p>
        ))}
      </blockquote>
      <figcaption className="flex flex-wrap items-center gap-x-2.5 gap-y-1 text-sm">
        <Avatar person={x.author} className="size-8 text-xs shadow-none" />
        <span className="font-medium">{x.author.name}</span>
        <span className="text-muted-foreground">{written(x.createdAt)}</span>
      </figcaption>
      {x.mine && (
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={() => setEditing(true)}>
            Edit
          </Button>
          <ConfirmButton
            label="Take back"
            title="Take back what you wrote?"
            description="It comes off the page for everyone."
            confirm="Take back"
            cancel="Keep it"
            disabled={remove.isPending}
            onConfirm={() => remove.mutate()}
          />
        </div>
      )}
      <FormError error={remove.error} />
    </figure>
  )
}

function Write({ person, circle, onDone }: { person: Person; circle: string; onDone: () => void }) {
  // A new key empties the form once its words are on the page.
  const [round, setRound] = useState(0)
  const first = person.name
  return (
    <div className="flex flex-col gap-3 rounded-3xl bg-secondary/70 p-5 sm:p-6">
      <WordsForm
        key={round}
        initial=""
        label={`What did you love most about ${first}?`}
        hint={`Everyone in ${circle} can read what you write here.`}
        submit="Add to the page"
        save={(body) => api(`/people/${person.id}/remembrances`, { method: 'POST', body: { body } })}
        onSaved={() => {
          setRound((n) => n + 1)
          onDone()
        }}
      />
    </div>
  )
}

function WordsForm({
  initial,
  label,
  hint,
  submit,
  save,
  onSaved,
  onCancel,
}: {
  initial: string
  label: string
  hint?: string
  submit: string
  save: (body: string) => Promise<unknown>
  onSaved: () => void
  onCancel?: () => void
}) {
  const id = useId()
  const [body, setBody] = useState(initial)
  const mutation = useMutation({ mutationFn: save, onSuccess: onSaved })
  function send(e: FormEvent) {
    e.preventDefault()
    if (body.trim()) mutation.mutate(body.trim())
  }
  return (
    <form onSubmit={send} className="grid gap-3">
      <div className="grid gap-2">
        <Label htmlFor={id}>{label}</Label>
        {hint && <p className="text-sub text-muted-foreground">{hint}</p>}
        <Textarea
          id={id}
          required
          maxLength={MAX}
          rows={3}
          className="bg-background"
          value={body}
          onChange={(e) => setBody(e.target.value)}
        />
      </div>
      <FormError error={mutation.error} />
      <div className="flex flex-wrap gap-2">
        <Button type="submit" disabled={mutation.isPending || !body.trim()}>
          {submit}
        </Button>
        {onCancel && (
          <Button type="button" variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
        )}
      </div>
    </form>
  )
}
